import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import { Refresh as RefreshIcon, Analytics as AnalyticsIcon } from '@mui/icons-material';
import React from 'react';
import { AnnotatedSection } from '../store/ocrStore';

interface AnnotationModalProps {
  open: boolean;
  annotation: AnnotatedSection | null;
  onClose: () => void;
  onConfirm: () => void;
  onRetry?: () => void;
  isAnalyzing?: boolean;
  error?: Error | null | any;
}

export const AnnotationModal: React.FC<AnnotationModalProps> = ({
  open,
  annotation,
  onClose,
  onConfirm,
  onRetry,
  isAnalyzing = false,
  error = null
}) => {
  if (!annotation) return null;

  const hasError = !!error;
  const canRetry = hasError && !!onRetry;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          width: '700px',
          maxWidth: '90vw',
          maxHeight: '65%'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AnalyticsIcon color="primary" />
          Annotation Preview & Analysis
        </Box>
      </DialogTitle>

      <DialogContent>
        {hasError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Analysis Failed</AlertTitle>
            {error.message || 'An error occurred while analyzing the annotation. Please try again.'}
          </Alert>
        )}

        {isAnalyzing && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '8px'
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
          </Box>
        )}

        <>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <img
                src={annotation.imageData}
                alt="Annotation preview"
                style={{
                  border: hasError ? '2px solid #f44336' : '1px dotted #1976d2',
                  borderRadius: '4px',
                  maxWidth: '100%',
                  maxHeight: '300px',
                  display: 'block',
                  opacity: hasError ? 0.7 : 1,
                  filter: hasError ? 'grayscale(20%)' : 'none'
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1 }}>
              Annotation Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Dimensions:</strong> {Math.round(annotation.width)} × {Math.round(annotation.height)} pixels
            </Typography>
          </Box>

          {!hasError && (
            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
              Click "Analyze" to process this section with OCR and extract the data.
              The results will be displayed in a table format on the right panel.
            </Typography>
          )}

          {hasError && (
            <Typography variant="body1" sx={{ mb: 2, color: 'SemanticError.main' }}>
              {canRetry
                ? 'You can try analyzing this annotation again or cancel to select a different area.'
                : 'Please cancel and try selecting a different area for annotation.'
              }
            </Typography>
          )}
        </>
      </DialogContent>

      <DialogActions sx={{ p: 4, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isAnalyzing}
        >
          Cancel
        </Button>

        {canRetry && (
          <Button
            variant="outlined"
            onClick={onRetry}
            disabled={isAnalyzing}
            startIcon={<RefreshIcon />}
            color="warning"
          >
            Retry Analysis
          </Button>
        )}

        {!hasError && (
          <Button
            variant="contained"
            onClick={onConfirm}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? 'Analyzing...' : 'Analyze'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};